import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  DatePicker,
  FiltrosChips,
  FiltrosDrawer,
  FiltrosGrupoBotoes,
  FiltrosSecao,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "../../../../../components";
import { value } from "../../../../../helpers";
import { formatDate } from "../../../../../utils";
import {
  MODOS,
  MODO_DESCRICOES,
  MODO_LABELS,
  PERIODO_PRESETS,
  formatarDataChip,
  intervaloPreset,
  modoVisualizacao,
} from "../fluxo-de-caixa";

const POPPER_SX = { sx: { zIndex: 9000 } };

function chipsAtivos(filtros, acessosEmpresas, aplicar) {
  const remover = (patch) => aplicar({ ...filtros, ...patch });
  const chips = [
    {
      key: "modo",
      icon: "ph-eye",
      label: MODO_LABELS[modoVisualizacao(filtros)],
      onDelete:
        filtros.previsto && filtros.realizado
          ? undefined
          : () => remover({ previsto: true, realizado: true }),
    },
    {
      key: "periodo",
      icon: "ph-calendar-blank",
      label: `${formatarDataChip(filtros.dataInicio)} – ${formatarDataChip(
        filtros.dataFim,
      )}`,
    },
  ];
  if (acessosEmpresas.length > 0) {
    filtros.empresas?.forEach((e) =>
      chips.push({
        key: `emp-${e.id}`,
        icon: "ph-buildings",
        label: e.razaoSocial,
        onDelete: () =>
          remover({ empresas: filtros.empresas.filter((x) => x.id !== e.id) }),
      }),
    );
  }
  if (filtros.considerarSaldoCaixa) {
    chips.push({
      key: "saldo-caixa",
      icon: "ph-wallet",
      label: "Saldo inicial dos caixas",
      onDelete: () =>
        remover({ considerarSaldoCaixa: false, bancosCaixas: [] }),
    });
    filtros.bancosCaixas?.forEach((conta) =>
      chips.push({
        key: `banco-caixa-${conta.id}`,
        icon: "ph-bank",
        label: conta.nome,
        onDelete: () =>
          remover({
            bancosCaixas: filtros.bancosCaixas.filter(
              (item) => item.id !== conta.id,
            ),
          }),
      }),
    );
  }
  return chips;
}

function FluxoCaixaFiltros({
  filtros,
  filtrosPadrao,
  onAplicar,
  listas,
  acessosEmpresas,
  draft,
  setDraft,
  open,
  setOpen,
}) {
  const setCampo = (campo, valor) =>
    setDraft((prev) => ({ ...prev, [campo]: valor }));

  const toggleSaldoCaixa = () =>
    setDraft((prev) => ({
      ...prev,
      considerarSaldoCaixa: !prev.considerarSaldoCaixa,
      bancosCaixas: prev.considerarSaldoCaixa ? [] : prev.bancosCaixas,
    }));

  const aplicar = () => {
    onAplicar(draft);
    setOpen(false);
  };

  const onChangeData = (campo, date) =>
    setCampo(
      campo,
      date && date !== "Invalid Date" ? formatDate.toSend(date) : "",
    );

  const toggleModo = (opcao) =>
    setDraft((prev) => {
      const next = { ...prev, [opcao.value]: !prev[opcao.value] };
      return !next.previsto && !next.realizado ? prev : next;
    });

  return (
    <>
      <FiltrosChips chips={chipsAtivos(filtros, acessosEmpresas, onAplicar)} />

      <FiltrosDrawer
        open={open}
        onClose={() => setOpen(false)}
        onLimpar={() => setDraft(filtrosPadrao)}
        onAplicar={aplicar}
      >
        {acessosEmpresas.length > 0 && (
          <FiltrosSecao titulo="Unidades">
            <Autocomplete
              multiple
              filterSelectedOptions
              options={acessosEmpresas}
              disableCloseOnSelect
              getOptionLabel={(opt) => opt?.razaoSocial ?? ""}
              isOptionEqualToValue={(opt, val) =>
                String(opt?.id) === String(val?.id)
              }
              value={draft.empresas ?? []}
              onChange={(_, val) => setCampo("empresas", val)}
              slotProps={{ popper: POPPER_SX }}
              renderInput={(params) => (
                <TextField {...params} label="Unidade(s)" />
              )}
            />
          </FiltrosSecao>
        )}

        <FiltrosSecao
          titulo="Visualização"
          descricao={MODO_DESCRICOES[modoVisualizacao(draft)]}
        >
          <FiltrosGrupoBotoes
            opcoes={MODOS}
            ativo={(opcao) => draft[opcao.value]}
            onSelecionar={toggleModo}
          />
        </FiltrosSecao>

        <FiltrosSecao titulo="Período" gap={1.5}>
          <Box display="flex" gap={1}>
            <DatePicker
              label="Data Inicial"
              format="dd/MM/yyyy"
              value={value.date(draft.dataInicio)}
              onChange={(date) => onChangeData("dataInicio", date)}
              sx={{ width: "100%" }}
            />
            <DatePicker
              label="Data Final"
              format="dd/MM/yyyy"
              value={value.date(draft.dataFim)}
              onChange={(date) => onChangeData("dataFim", date)}
              sx={{ width: "100%" }}
            />
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.75}>
            {PERIODO_PRESETS.map((preset) => (
              <Chip
                key={preset.label}
                size="small"
                clickable
                variant="outlined"
                label={preset.label}
                onClick={() =>
                  setDraft((prev) => ({ ...prev, ...intervaloPreset(preset) }))
                }
              />
            ))}
          </Box>
        </FiltrosSecao>

        <FiltrosSecao titulo="Saldo acumulado">
          <Tooltip
            title="Inicia o acúmulo pelo saldo do dia (entradas menos saídas) dos caixas na véspera do período, ignorando o histórico. Desmarcado, parte do saldo acumulado de todas as movimentações anteriores ao período."
            placement="top"
            arrow
          >
            <FormControlLabel
              sx={{ m: 0, alignItems: "center" }}
              label={
                <Typography variant="body2">
                  Considerar saldo dos caixas
                </Typography>
              }
              control={
                <Checkbox
                  checked={draft.considerarSaldoCaixa}
                  onChange={toggleSaldoCaixa}
                  color="primary"
                />
              }
            />
          </Tooltip>

          {draft.considerarSaldoCaixa && (
            <Autocomplete
              multiple
              filterSelectedOptions
              disableCloseOnSelect
              options={listas?.contas ?? []}
              noOptionsText="Sem opções"
              getOptionLabel={(opt) => opt?.nome ?? ""}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={draft.bancosCaixas ?? []}
              onChange={(_, val) => setCampo("bancosCaixas", val)}
              slotProps={{ popper: POPPER_SX }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Conta"
                  helperText="Em branco, considera todos os bancos e caixas"
                />
              )}
            />
          )}
        </FiltrosSecao>
      </FiltrosDrawer>
    </>
  );
}

export default FluxoCaixaFiltros;
